import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Loader2, Phone } from 'lucide-react';

export interface FaltaNumeroHa {
  indice: number;
  ddd: string;
}

interface NumeroLinhaHaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  razaoSocial: string;
  produto: string;
  faltas: FaltaNumeroHa[];
  erro: string | null;
  isLoading: boolean;
  onConfirm: (numeros: string[]) => void;
}

/** Os 9 dígitos do celular, aceitando o número colado com DDD. Mesma régua do backend (numero-linha-ha.ts). */
function normalizar(valor: string, ddd: string): string | null {
  let digitos = valor.replace(/\D/g, '');
  if (digitos.startsWith('55') && digitos.length === 13) digitos = digitos.slice(2);
  if (digitos.length === 11 && (!ddd || digitos.startsWith(ddd))) digitos = digitos.slice(2);
  return /^9\d{8}$/.test(digitos) ? digitos : null;
}

/**
 * ⛔ HA não finaliza sem o número da linha (Douglas 30/09/2026). A linha nova é
 * vendida como "Número Novo"; na Inspeção concluída ela já existe na Claro e a
 * BKO copia o número do SOLAR. Ele entra no lugar da linha do pedido — é o que
 * a Planilha de Pedidos e a validação do eSIM passam a mostrar.
 */
export function NumeroLinhaHaDialog({
  open,
  onOpenChange,
  razaoSocial,
  produto,
  faltas,
  erro,
  isLoading,
  onConfirm,
}: NumeroLinhaHaDialogProps) {
  const [numeros, setNumeros] = useState<string[]>([]);

  useEffect(() => {
    if (open) setNumeros(faltas.map(() => ''));
  }, [open, faltas]);

  const normalizados = faltas.map((f, i) => normalizar(numeros[i] || '', f.ddd));
  const podeConfirmar = normalizados.every(Boolean) && !isLoading;

  return (
    <Dialog open={open} onOpenChange={(o) => !isLoading && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-primary" />
            Número da linha ativada
          </DialogTitle>
          <DialogDescription>
            {razaoSocial}{produto ? ` · ${produto}` : ''}
            <br />
            A linha nova já existe na Claro. Copie o número do SOLAR para o pedido
            antes de finalizar: ele aparece na Planilha de Pedidos e na validação do eSIM.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          {faltas.map((f, i) => {
            const digitado = (numeros[i] || '').trim();
            const invalido = digitado.length > 0 && !normalizados[i];
            return (
              <div key={f.indice} className="space-y-1">
                <Label htmlFor={`ha-numero-${i}`} className="text-xs font-semibold">
                  {faltas.length > 1 ? `Linha ${i + 1}` : 'Número da linha'}
                </Label>
                <div className="flex gap-2">
                  <Input value={f.ddd} readOnly disabled className="w-14 text-center tabular-nums" aria-label="DDD" />
                  <Input
                    id={`ha-numero-${i}`}
                    placeholder="99999-9999"
                    inputMode="numeric"
                    autoFocus={i === 0}
                    value={numeros[i] || ''}
                    onChange={(e) => setNumeros((atual) => atual.map((v, j) => (j === i ? e.target.value : v)))}
                    disabled={isLoading}
                    className="flex-1 tabular-nums"
                  />
                </div>
                {invalido && (
                  <p className="text-[10px] text-destructive">Celular com 9 dígitos, começando com 9.</p>
                )}
              </div>
            );
          })}
          {faltas.length > 1 && (
            <p className="text-[10px] text-muted-foreground">Um número por linha do item. O DDD vem do pedido.</p>
          )}
          {erro && <p className="text-[11px] text-destructive">{erro}</p>}
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Voltar
          </Button>
          <Button
            className="flex-1 bg-primary hover:bg-primary text-white"
            disabled={!podeConfirmar}
            onClick={() => onConfirm(normalizados as string[])}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Salvar e finalizar 100%
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
